import { html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import { NAMESPACE } from "@/constants";
import { genRandSeed } from "@/utils";

import { Auth } from "@saasbase-io/loginflow-websdk";
import { OtpResendParts, OtpResendProps as Props } from "./otp.types";
import { OTP_RESEND_DEFAULT_PROPS as DEFAULT_PROPS } from "./otp.defs";

@customElement("sb-otp-resend")
export class SbOtpResend extends LitElement implements Props {
  static readonly ROOT = "otp-resend";
  static readonly COUNTDOWN = "countdown";
  static readonly RESEND_BUTTON = "resend-button";

  static readonly TAG = `${NAMESPACE}-${SbOtpResend.ROOT}`;

  static readonly PARTS: Record<OtpResendParts, string> = {
    root: SbOtpResend.ROOT,
    countdown: `${SbOtpResend.ROOT}-${SbOtpResend.COUNTDOWN}`,
    resendButton: `${SbOtpResend.ROOT}-${SbOtpResend.RESEND_BUTTON}`,
  };

  /* ------------------------------------------------------------ */

  /**
   * Substring used in generated part IDs.
   */
  @property({ type: String })
  seed = genRandSeed();

  /**
   * Custom ID used as the root part ID and as the base of subpart IDs.
   */
  @property({ type: String, attribute: "custom-id" })
  customId?: string;

  /**
   * Countdown duration in seconds.
   */
  @property({ type: Number })
  duration: number = DEFAULT_PROPS.duration;

  /**
   * Event forwarded to Auth.processLoginflowEvent when the resend button is clicked.
   */
  @property({ type: String, attribute: "resend-event" })
  resend_event: string = DEFAULT_PROPS.resend_event;

  /**
   * Label for the resend button (visible when the countdown has finished).
   */
  @property({ type: String, attribute: "resend-button-label" })
  resend_button_label: string = DEFAULT_PROPS.resend_button_label;

  /**
   * Text displayed before the remaining time, e.g. "Resend code in ".
   */
  @property({ type: String, attribute: "count-down-text" })
  count_down_text: string = DEFAULT_PROPS.count_down_text;

  /* ---------------------------------------- */

  @state()
  private _remaining: number = 0;

  private _timer: number | undefined;

  /* ------------------------------------------------------------ */

  protected createRenderRoot() {
    return this;
  }

  /* ------------------------------------------------------------ */

  get rootId() {
    return this.customId ?? `${SbOtpResend.TAG}-${this.seed}`;
  }

  get countdownId() {
    return `${this.rootId}-${SbOtpResend.COUNTDOWN}`;
  }

  get resendButtonId() {
    return `${this.rootId}-${SbOtpResend.RESEND_BUTTON}`;
  }

  /* ------------------------------------------------------------ */

  connectedCallback() {
    super.connectedCallback();
    this._startCountdown();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._clearTimer();
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has("duration")) {
      this._startCountdown();
    }
  }

  /* ------------------------------------------------------------ */

  private _startCountdown() {
    this._clearTimer();
    const dur = this.duration;
    if (dur <= 0) {
      this._remaining = 0;
      return;
    }
    this._remaining = dur;
    this._timer = window.setInterval(() => {
      this._remaining = Math.max(0, this._remaining - 1);
      if (this._remaining <= 0) {
        this._clearTimer();
      }
    }, 1000);
  }

  private _clearTimer() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = undefined;
    }
  }

  private _handleResendClick = () => {
    Auth.processLoginflowEvent({ event: this.resend_event });
    // restart the countdown after a successful resend request
    this._startCountdown();
  };

  /* ---------------------------------------- */

  render() {
    const showCountdown = this._remaining > 0;

    return html`
      <div part=${SbOtpResend.PARTS.root} id=${this.rootId}>
        ${showCountdown
          ? html`
              <rtg-typography-muted
                part=${SbOtpResend.PARTS.countdown}
                id=${this.countdownId}
              >
                ${this.count_down_text}${this._remaining}s
              </rtg-typography-muted>
            `
          : html`
              <rtg-button
                part=${SbOtpResend.PARTS.resendButton}
                id=${this.resendButtonId}
                variant="link"
                @click=${this._handleResendClick}
              >
                ${this.resend_button_label}
              </rtg-button>
            `}
      </div>
    `;
  }
}
